import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import unionBy from 'lodash/unionBy';
import type { RootState } from '@/rootReducer';
import {
	ChatGPT,
	ChatGPTUnfilled,
	CheckMarkGreenUnfilled,
	CheckboxChecked,
	CheckboxUnchecked,
	CheckmarkGreenCircleFilled,
	CloseThin,
	EllipsisOrange,
	ExclamationMarkRed,
	FilterCircle,
	GoldCalendar,
	LightbulbIcon,
	SendAllToggleOn,
	Toggle,
	TrashFullSquare
} from '@/resources/assets/images';
import QuestionMarkPNG from '@/assets/images/question-mark.png';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import BusinessPreviewEmailModal from '@/modules/dashboard/components/BusinessPreviewEmailModal/BusinessPreviewEmailModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import RichEditor from '@/components/RichEditor/RichEditor';
import Loader, { Status } from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import { normalizeHTML } from '@/utils/normalizeHTML';
import { UserRole } from '@/modules/user/userTypes';
import type { FilterProps } from '@/components/molecules/Filters';
import type { Email, EmailTemplate } from '../../emailTypes';
import {
	emailClearAIRegenerate,
	emailGetAISnippets,
	emailRegenAISnippet,
	emailSendAll,
	emailSendAllClear
} from '../../emailActions';
import './SendAllModal.scss';

type Props = FilterProps & {
	activeBusiness: Business | undefined;
	setActiveBusiness: (x: Business) => void;
	selectedTemplate: EmailTemplate | undefined;
	setDeleteDataIndex: (x: number) => void;
	openScheduledSendCancelModal: () => void;
	setShowSendAll: (x: boolean) => void;
	closeEmailModal: () => void;
	setCustomEmailModalDisplay: (x: boolean) => void;
	sendAllMode: boolean;
	setSendAllMode: (x: boolean) => void;
	classicMode: () => void;
	showAiLoadingModal: boolean;
};

const SendAllModal = ({
	activeBusiness,
	setActiveBusiness,
	selectedTemplate,
	setDeleteDataIndex,
	openScheduledSendCancelModal,
	setShowSendAll,
	closeEmailModal,
	setCustomEmailModalDisplay,
	sendAllMode,
	setSendAllMode,
	classicMode,
	showAiLoadingModal,
	appliedFilters
}: Props) => {
	const [
		user,
		allBusinesses,
		list,
		scheduledEmailActivity,
		cancelScheduledEmailSuccess,
		sendAllLoading,
		sendAllSuccess,
		sendAllFailure,
		monthlyEmailLimit,
		aiText,
		aiCredits,
		aiLoading,
		aiRegenerateLoading,
		aiRegenerateFailure,
		aiRegenerateSuccessId,
		aiRegenerated
	] = useSelector((state: RootState) => [
		state.user.getIn(['authorize', 'user']).toJS(),
		state.dashboard.getIn(['businesses', 'allData']).toJS(),
		state.dashboard.getIn(['lists', 'selected']).toJS(),
		state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
		state.email.getIn(['email', 'scheduled', 'cancel', 'success']),
		state.email.getIn(['email', 'sendAll', 'loading']),
		state.email.getIn(['email', 'sendAll', 'success']),
		state.email.getIn(['email', 'sendAll', 'failure']),
		state.email.getIn(['email', 'monthlyLimit']),
		state.email.getIn(['ai', 'text']).toJS(),
		state.email.getIn(['ai', 'credits']),
		state.email.getIn(['ai', 'loading']),
		state.email.getIn(['ai', 'regenerate', 'loading']).toJS(),
		state.email.getIn(['ai', 'regenerate', 'failure']),
		state.email.getIn(['ai', 'regenerate', 'successId']),
		state.email.getIn(['ai', 'regenerate', 'data']).toJS()
	]);
	const [bodyContextMenuValue, setBodyContextMenuValue] = useState<
		string | undefined
	>();
	const [showSendAllFinalModal, setShowSendAllFinalModal] = useState(false);
	const [approvedEmails, setApprovedEmails] = useState([]);
	const [approvedBusinessList, setApprovedBusinessList] = useState<
		Business[]
	>([]);
	const [scheduledBusinesses, setScheduledBusinesses] = useState<Business[]>(
		[]
	);
	const [openFilter, setOpenFilter] = useState<boolean>(false);
	const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
	const [initialRender, setInitialRender] = useState<boolean>(false);
	const dispatch = useDispatch();
	const sendAll = (emails: Email[]) => dispatch(emailSendAll({ emails }));
	const getAISnippets = (
		businessIds: string[],
		listId: string,
		emailTemplate: EmailTemplate
	) => dispatch(emailGetAISnippets({ businessIds, listId, emailTemplate }));
	const regenAISnippet = (
		businessId: string,
		listId: string,
		emailTemplate: EmailTemplate,
		aiSnippetsId: string
	) =>
		dispatch(
			emailRegenAISnippet({
				businessId,
				listId,
				emailTemplate,
				aiSnippetsId
			})
		);
	const clearAIRegenerate = () => dispatch(emailClearAIRegenerate());
	const sendAllClear = () => dispatch(emailSendAllClear());

	const replaceVariables = (value: string): string =>
		value
			.replaceAll(/\[business_name\]/gi, activeBusiness?.name ?? '')
			.replaceAll(/\[list_name\]/gi, list?.name ?? '');

	const [to, setTo] = useState<string>('');
	const [subject, setSubject] = useState<string>(
		selectedTemplate?.subject
			? replaceVariables(selectedTemplate.subject)
			: ''
	);
	const [makeRender, setMakeRender] = useState<boolean>(false);
	const nylasSynced = useMemo(
		() =>
			user?.nylasSyncStatus === 'running' ||
			user?.nylasSyncStatus === 'nothing' ||
			user?.nylasSyncStatus === undefined ||
			user?.nylasSyncStatus === 'partial',
		[user?.nylasSyncStatus]
	);
	const [editedEmailsTo, setEditedEmailsTo] = useState<{
		[x: string]: string;
	}>({});
	const editedEmails = useRef<{ [x: string]: Email }>({});
	const [displayAllDuplicates, setDisplayAllDuplicates] =
		useState<boolean>(false);
	const [duplicateGroup, setDuplicateGroup] = useState<
		DuplicateGroup | Email[][]
	>([[]]);

	const bodyDefaultValue = () => {
		if (
			aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[
				`${allBusinesses[0]?._id}`
			]
		) {
			return replaceVariables(
				aiText[`${list.id}`][`${selectedTemplate?.id}`][
					`${allBusinesses[0]?._id}`
				]?.value
			);
		}

		if (selectedTemplate?.body) {
			return replaceVariables(selectedTemplate.body);
		}

		return '';
	};

	// Finding all instances of duplicate emails
	const findDuplicates = (array: Email[]) => {
		const valueMap = new Map();
		const duplicates: Email[] = [];

		array.forEach((item: Email) => {
			const value = item['email'];

			if (valueMap.has(value)) {
				if (valueMap.get(value) !== null) {
					duplicates.push(valueMap.get(value));
					valueMap.set(value, null);
				}
				duplicates.push(item);
			} else {
				valueMap.set(value, item);
			}
		});
		const separatedDuplicates = separateDuplicates(duplicates);
		setDuplicateGroup(separatedDuplicates);
		return separatedDuplicates;
	};

	const removeAllDuplicates = () => {
		duplicateGroup.forEach((item: Email[]) => {
			if (item.length > 1) {
				for (let i = 1; i < item.length; i++) {
					duplicateApproveButtonClickHandler(item[i]);
				}
			}
		});
	};

	// Separating them into the group of separate duplicates
	const separateDuplicates = (duplicateList: Email[]) => {
		const emailMap = new Map();

		// Populate the map with emails as keys and arrays of objects as values
		duplicateList.forEach(item => {
			const { email } = item;
			if (!emailMap.has(email)) {
				emailMap.set(email, []);
			}
			emailMap.get(email).push(item);
		});

		// Convert the map values to a list of lists
		const groupedLists = Array.from(emailMap.values());

		return groupedLists;
	};

	interface DuplicateGroup {
		data: Email[][];
	}

	// React component to display grouped data
	const DisplayDuplicates = (duplicateGroup: { data: Email[] }) => {
		const [expandedGroupIndex, setExpandedGroupIndex] = useState<
			number | null
		>(null);
		const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(true);

		const toggleGroup = (index: number | null) => {
			setExpandedGroupIndex(prevIndex =>
				prevIndex === index ? null : index
			);
		};

		const closeModal = () => {
			setIsDuplicateModalOpen(false);
			setDisplayAllDuplicates(false);
			setDuplicateGroup([[]]);
			setExpandedGroupIndex(null);
		};

		return (
			<div className="display-duplicates-holder">
				{isDuplicateModalOpen && (
					<div className="duplicates-modal">
						<div className="duplicates-modal-content">
							<span
								className="close-duplicates-modal"
								onClick={closeModal}
							>
								&times;
							</span>
							<h1 className="duplicates-modal-header">
								Duplicate Email Addresses
							</h1>
							<div className="display-duplicates">
								<div className="left-column-content">
									<span className="left-column-header">
										Duplicate Email Addresses
									</span>
									<div className="left-column">
										{Array.isArray(duplicateGroup.data) &&
											duplicateGroup.data.map(
												(group, index) => (
													<div
														key={index}
														className="group-item"
														onClick={() =>
															toggleGroup(index)
														}
													>
														Email: {group[0]?.email}
													</div>
												)
											)}
									</div>
								</div>
								<div className="right-column-content">
									<span className="right-column-header">
										Businesses
									</span>
									<div className="right-column">
										{expandedGroupIndex !== null &&
											expandedGroupIndex <
												duplicateGroup.data.length && (
												<div className="inner-duplicate-by-business">
													{duplicateGroup.data[
														expandedGroupIndex
													].map(
														(
															item: {
																_id:
																	| string
																	| undefined;
																name: string;
																email:
																	| string
																	| null
																	| undefined;
															},
															innerIndex:
																| number
																| null
																| undefined
														) => (
															<div
																id={item._id}
																key={innerIndex}
																className="group-item"
															>
																<span
																	className={`duplicate-name-${innerIndex}`}
																>
																	{item.name}
																</span>
																<br />
																<span
																	className={`duplicate-email-${innerIndex}`}
																>
																	{item.email}
																</span>

																<button
																	className={`send-all-modal-approve-button ${duplicateApproveButtonClass(item)} `}
																	onClick={() => {
																		duplicateApproveButtonClickHandler(
																			item
																		);
																	}}
																	type="button"
																>
																	{displayApproveButtonText()}
																</button>
															</div>
														)
													)}
												</div>
											)}
									</div>
								</div>
							</div>
							<div className="duplicates-modal-footer">
								<button
									className="delete-all-duplicates-button"
									onClick={() => {
										removeAllDuplicates();
									}}
								>
									Remove All Duplicates
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const body = useRef<string>(bodyDefaultValue());

	const contactedCalloutText = (date: string) => {
		const sentDate = dayjs(date);
		const currentDate = dayjs(new Date());
		const hoursDiff = currentDate.diff(sentDate, 'hour');
		const daysDiff = currentDate.diff(sentDate, 'day');
		let text;

		if (hoursDiff < 24) {
			text = `less than a day ago`;
		} else if (hoursDiff >= 24 && hoursDiff < 48) {
			text = 'a day ago';
		} else if (hoursDiff >= 48) {
			text = `${daysDiff} days ago`;
		} else {
			text = 'already';
		}

		return <div>You emailed this business {text}.&nbsp;</div>;
	};

	const pullFirstUserAddedEmail = (b: Business): string => {
		if (!b.userAddedData) {
			return '';
		}

		for (let i = 0; i < b.userAddedData.length; i++) {
			if (
				b.userAddedData[i].type === 'email' &&
				!b.userAddedData[i].emailLowPriority
			) {
				return b.userAddedData[i].data;
			}
		}
		return '';
	};

	const collectApprovedEmails = () => {
		const elements = Array.from(document.querySelectorAll('.approved'));
		const approvedBusinessIds = elements.map(el => {
			if (!el.classList.contains('unapproved')) {
				return el.id;
			}
			return undefined;
		});
		const approvedSet = new Set(approvedBusinessIds);
		const approvedBusinesses = allBusinesses.filter((b: Business) =>
			approvedSet.has(b._id)
		);

		if (
			approvedBusinesses.length < approvedBusinessList.length &&
			selectedFilters.length === 0
		) {
			setApprovedBusinessList(approvedBusinesses);
		} else {
			setApprovedBusinessList(
				unionBy(approvedBusinessList, approvedBusinesses, '_id')
			);
		}

		const emailsApproved = approvedBusinesses.map((b: Business) => {
			const el = document.getElementById(`${b._id}`);

			if (el?.classList.contains('approved')) {
				const replaceVar = (value: string): string =>
					value
						.replaceAll(/\[business_name\]/gi, b.name ?? '')
						.replaceAll(/\[list_name\]/gi, list.name ?? '');

				const bodyValue = () => {
					if (
						aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[
							`${b?._id}`
						] &&
						selectedTemplate?.body?.includes('[ai_snippet]')
					) {
						return (
							editedEmails.current[b?._id as keyof Email]?.body ??
							aiText[`${list.id}`][`${selectedTemplate?.id}`][
								`${b?._id}`
							].value
						);
					}
					return (
						editedEmails.current[`${b?._id}`]?.body ??
						selectedTemplate?.body
					);
				};

				return {
					subject: replaceVar(
						editedEmails.current[`${b?._id}`]?.subject ??
							(selectedTemplate?.subject as string)
					),
					body: replaceVar(bodyValue()),
					businessId: b._id,
					business: b,
					name: b.name,
					listId: list.id,
					email:
						editedEmailsTo[b?._id as keyof Email] ??
						b?.email ??
						b?.facebook?.email ??
						b?.web?.email ??
						pullFirstUserAddedEmail(b),
					templateId: selectedTemplate?.id,
					isAiSnippetEmail:
						selectedTemplate?.body?.includes('[ai_snippet]')
				};
			}
		});

		setApprovedEmails(emailsApproved);
	};

	const sendAllModalLeftRef = useRef<HTMLDivElement>(null);

	// Set the initial filters based on the query parameters
	useEffect(() => {
		if (appliedFilters?.contactedYes) {
			setSelectedFilters(prev => {
				if (!prev.includes('Contacted')) {
					return [...prev, 'Contacted'];
				}
				return prev;
			});
		}

		if (appliedFilters?.contactedNo) {
			setSelectedFilters(prev => {
				if (!prev.includes('Not contacted')) {
					return [...prev, 'Not contacted'];
				}
				return prev;
			});
		}

		if (appliedFilters?.scheduledYes) {
			setSelectedFilters(prev => {
				if (!prev.includes('Scheduled')) {
					return [...prev, 'Scheduled'];
				}
				return prev;
			});
		}

		if (appliedFilters?.scheduledNo) {
			setSelectedFilters(prev => {
				if (!prev.includes('Not scheduled')) {
					return [...prev, 'Not scheduled'];
				}
				return prev;
			});
		}
	}, [
		appliedFilters?.contactedNo,
		appliedFilters?.contactedYes,
		appliedFilters?.scheduledNo,
		appliedFilters?.scheduledYes
	]);

	const filteredBusinesses = useMemo(() => {
		let tempFilteredBusinesses = allBusinesses;

		const approvedBusinessIds = approvedBusinessList.map(
			(b: Business) => b._id
		);
		const approvedSet = new Set(approvedBusinessIds);

		// TODO: This creates a coupling with the selected filters and their values.
		// Whenever we change the filters, we might want to change those as well.
		if (selectedFilters.includes('Approved')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => approvedSet.has(b._id)
			);
		}

		if (selectedFilters.includes('Not approved')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => !approvedSet.has(b._id)
			);
		}

		if (selectedFilters.includes('Contacted')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => b?.isContacted
			);
		}

		if (selectedFilters.includes('Not contacted')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => !b?.isContacted
			);
		}

		if (selectedFilters.includes('Scheduled')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => scheduledEmailActivity[`${b?._id}`]
			);
		}

		if (selectedFilters.includes('Not scheduled')) {
			tempFilteredBusinesses = tempFilteredBusinesses.filter(
				(b: Business) => !scheduledEmailActivity[`${b?._id}`]
			);
		}

		return tempFilteredBusinesses;
	}, [
		allBusinesses,
		approvedBusinessList,
		scheduledEmailActivity,
		selectedFilters
	]);

	const approveLimit =
		filteredBusinesses.length < monthlyEmailLimit
			? filteredBusinesses.length
			: monthlyEmailLimit;

	const approveAll = () => {
		for (let i = 0; i < approveLimit; i++) {
			const b = filteredBusinesses[i];

			// If there's no AI Snippet generated here, don't approve it
			if (
				selectedTemplate?.body?.includes('ai_snippet') &&
				!aiText?.[`${list?.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${b?._id}`
				]
			)
				continue;

			const el = document.querySelector(`.element-${b?._id}`);
			const toggle = document.querySelector(`.toggle-${b?._id}`);

			if (
				el?.classList.contains('approved') &&
				el?.classList.contains('unapproved') &&
				toggle?.classList.contains('unapproved')
			) {
				toggle.classList.remove('unapproved');
				el.classList.remove('unapproved');
			}
		}

		collectApprovedEmails();
	};

	const approveButtonClickHandler = () => {
		const el = document.querySelector(
			`.element-${activeBusiness?._id}`
		) as HTMLElement | null;

		if (el?.dataset?.position) {
			const position = parseInt(el.dataset.position, 10);

			if (position < filteredBusinesses.length - 1) {
				const overallPosition = position * 64;

				if (sendAllModalLeftRef?.current) {
					const windowScrollPosition =
						sendAllModalLeftRef.current.scrollTop +
						sendAllModalLeftRef.current.clientHeight -
						128;

					if (overallPosition > windowScrollPosition) {
						sendAllModalLeftRef.current.scroll(
							0,
							sendAllModalLeftRef.current.scrollTop + 64
						);
					}
				}
			}
		}

		if (
			el?.dataset?.position &&
			parseInt(el.dataset.position, 10) < filteredBusinesses.length - 1
		) {
			setActiveBusiness(
				filteredBusinesses[Number(el.dataset.position) + 1]
			);
		}

		if (scheduledEmailActivity[`${activeBusiness?._id}`]) {
			return;
		}

		const toggle = document.querySelector(`.toggle-${activeBusiness?._id}`);

		if (
			el?.classList.contains('approved') &&
			el?.classList.contains('unapproved') &&
			toggle?.classList.contains('unapproved')
		) {
			// Approving email
			toggle.classList.remove('unapproved');
			el.classList.remove('unapproved');
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'user_approved_business_send_all',
				properties: {
					business: {
						_id: activeBusiness?._id,
						address: activeBusiness?.address,
						name: activeBusiness?.name
					},
					method: 'button'
				}
			});
		} else if (
			el?.classList.contains('approved') &&
			!el?.classList.contains('unapproved') &&
			!toggle?.classList.contains('unapproved') &&
			toggle
		) {
			// Disapproving email
			toggle.classList.add('unapproved');
			el.classList.add('unapproved');
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'user_unapproved_business_send_all',
				properties: {
					business: {
						_id: activeBusiness?._id,
						address: activeBusiness?.address,
						name: activeBusiness?.name
					},
					method: 'button'
				}
			});
		}

		collectApprovedEmails();
	};

	const displayApproveButtonText = () => {
		const toggle = document.querySelector(`.toggle-${activeBusiness?._id}`);

		if (scheduledEmailActivity[`${activeBusiness?._id}`]) {
			return 'Skip';
		}

		if (toggle?.classList.contains('unapproved')) {
			return 'Approve';
		}

		return 'Unapprove';
	};

	const approveButtonClass = () => {
		const toggle = document.querySelector(`.toggle-${activeBusiness?._id}`);

		if (toggle?.classList.contains('unapproved')) {
			return '';
		}

		return 'unapprove';
	};

	const duplicateApproveButtonClass = (item: {
		_id?: string | undefined;
		name?: string;
		email?: string | undefined;
		businessId?: string;
	}) => {
		const toggle = document.querySelector(`.toggle-${item?.businessId}`);
		if (toggle?.classList.contains('unapproved')) {
			return '';
		}

		return 'unapprove';
	};

	const getElementBySelector = (selector: string): HTMLElement | null =>
		document.querySelector(selector) as HTMLElement | null;

	const isValidPosition = (position: string | undefined): boolean =>
		position !== undefined && !Number.isNaN(parseInt(position, 10));

	const scrollIfNeeded = (
		el: HTMLElement,
		position: number,
		ref: React.RefObject<HTMLElement | null>
	): void => {
		if (ref.current) {
			const overallPosition = position * 64;
			const windowScrollPosition =
				ref.current.scrollTop + ref.current.clientHeight - 128;

			if (overallPosition > windowScrollPosition) {
				ref.current.scroll(0, ref.current.scrollTop + 64);
			}
		}
	};

	const handleApproval = (
		el: HTMLElement,
		toggle: HTMLElement | null
	): void => {
		if (
			el.classList.contains('approved') &&
			el.classList.contains('unapproved') &&
			toggle?.classList.contains('unapproved')
		) {
			// Approving email
			toggle.classList.remove('unapproved');
			el.classList.remove('unapproved');
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'user_approved_business_send_all',
				properties: {
					business: {
						_id: activeBusiness?._id,
						address: activeBusiness?.address,
						name: activeBusiness?.name
					},
					method: 'button'
				}
			});
		} else if (
			el.classList.contains('approved') &&
			!el.classList.contains('unapproved') &&
			!toggle?.classList.contains('unapproved')
		) {
			// Disapproving email
			toggle?.classList.add('unapproved');
			el.classList.add('unapproved');
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'user_unapproved_business_send_all',
				properties: {
					business: {
						_id: activeBusiness?._id,
						address: activeBusiness?.address,
						name: activeBusiness?.name
					},
					method: 'button'
				}
			});
		}
	};

	const duplicateApproveButtonClickHandler = (item: any): void => {
		const el = getElementBySelector(`.element-${item?.businessId}`);
		const position = el?.dataset?.position;

		if (position && isValidPosition(position)) {
			const pos = parseInt(position, 10);

			if (pos < filteredBusinesses.length - 1) {
				scrollIfNeeded(el, pos, sendAllModalLeftRef);
				setActiveBusiness(filteredBusinesses[pos + 1]);
			}
		}

		if (scheduledEmailActivity[item?.businessId]) {
			return;
		}

		const toggle = getElementBySelector(`.toggle-${item.businessId}`);

		if (el && toggle) {
			handleApproval(el, toggle);
		}

		collectApprovedEmails();
	};

	useEffect(() => {
		findDuplicates(approvedEmails);
	}, [approvedEmails, editedEmailsTo]);

	useEffect(() => {
		if (!isEmpty(activeBusiness) && !aiLoading) {
			if (selectedTemplate?.subject) {
				setSubject(
					replaceVariables(
						editedEmails.current[`${activeBusiness?._id}`]
							?.subject ?? selectedTemplate.subject
					)
				);
			}

			if (selectedTemplate?.body) {
				if (
					aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[
						`${activeBusiness?._id}`
					] &&
					selectedTemplate.body?.includes('[ai_snippet]')
				) {
					body.current =
						editedEmails.current[activeBusiness?._id as keyof Email]
							?.body ??
						aiText[`${list.id}`][`${selectedTemplate?.id}`][
							`${activeBusiness?._id}`
						].value;
					setMakeRender(!makeRender);
				} else {
					body.current =
						editedEmails.current[`${activeBusiness?._id}`]?.body ??
						selectedTemplate.body;
					setMakeRender(!makeRender);
				}
			}

			setTo(
				editedEmailsTo[`${activeBusiness?._id}`] ??
					activeBusiness?.email ??
					activeBusiness?.facebook?.email ??
					activeBusiness?.web?.email ??
					pullFirstUserAddedEmail(activeBusiness)
			);
		}

		allBusinesses.forEach((b: Business) => {
			const el = document.querySelector(`.element-${b._id}`);
			const toggle = document.querySelector(`.toggle-${b._id}`);

			if (
				!toggle?.classList.contains('unapproved') &&
				el?.classList.contains('unapproved')
			) {
				el.classList.remove('unapproved');
			}
		});
	}, [activeBusiness?._id]);

	useEffect(() => {
		if (
			!showAiLoadingModal &&
			selectedTemplate?.body?.includes('[ai_snippet]') &&
			aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[
				`${activeBusiness?._id}`
			]
		) {
			body.current =
				aiText[`${list.id}`][`${selectedTemplate?.id}`][
					`${activeBusiness?._id}`
				].value;
			setMakeRender(!makeRender);
		}
	}, [showAiLoadingModal]);

	useEffect(() => {
		if (aiRegenerateSuccessId) {
			editedEmails.current = {
				...editedEmails.current,
				[aiRegenerateSuccessId ?? 0]: {
					...editedEmails.current[`${aiRegenerateSuccessId}`],
					body: aiText[`${list?.id}`][`${selectedTemplate?.id}`][
						`${aiRegenerateSuccessId}`
					].value
				}
			};

			if (activeBusiness?._id === aiRegenerateSuccessId) {
				body.current =
					aiText?.[`${list?.id}`]?.[`${selectedTemplate?.id}`]?.[
						`${aiRegenerateSuccessId}`
					].value;
			}
			clearAIRegenerate();
		}
	}, [aiRegenerateSuccessId]);

	const fetchingAiSnippets = (template: EmailTemplate) => {
		if (!template?.id || !list?.businessIds?.length) return;

		const aiTextCount = Object.keys(
			aiText?.[list?.id]?.[template?.id] ?? {}
		)?.length;

		const batchSize = 50;

		const start = Math.floor(aiTextCount / batchSize) * batchSize;
		const end =
			aiCredits >= batchSize
				? (Math.ceil(aiTextCount / batchSize) + 1) * batchSize
				: start + aiCredits;

		const selectedBusinessIds = list?.businessIds?.slice(start, end) || [];

		getAISnippets([...selectedBusinessIds], list?.id, template);
	};

	useEffect(() => {
		if (
			!selectedTemplate ||
			!selectedTemplate?.id ||
			!selectedTemplate?.body?.includes('[ai_snippet]') ||
			!activeBusiness ||
			aiCredits <= 0 ||
			aiLoading
		) {
			return;
		}

		const activeSnippet = aiText?.[list?.id]?.[selectedTemplate?.id] ?? {};

		if (
			list?.businessIds?.length > 0 &&
			Object.keys(activeSnippet)?.length < list?.businessIds?.length
		) {
			fetchingAiSnippets(selectedTemplate);
		}
	}, [aiLoading]);

	const filterOptions = [
		'Approved',
		'Not approved',
		'Scheduled',
		'Not scheduled',
		'Contacted',
		'Not contacted'
	];

	const displayToggleHovertext = (b: Business) => {
		const el = document.querySelector(`.element-${b._id}`);

		if (
			el?.classList.contains('approved') &&
			el?.classList.contains('unapproved')
		) {
			return (
				<span className="send-all-modal-single-email-item-toggle-switch-unapproved-hovertext">
					Unapproved
				</span>
			);
		}

		if (
			el?.classList.contains('approved') &&
			!el?.classList.contains('unapproved')
		) {
			return (
				<span className="send-all-modal-single-email-item-toggle-switch-approved-hovertext">
					Approved
				</span>
			);
		}
	};

	const filterDropdownRef = useRef<HTMLDivElement>(null);
	const filterButtonRef = useRef<HTMLButtonElement>(null);

	const closeDropdown = (e: MouseEvent) => {
		if (
			filterDropdownRef?.current &&
			filterButtonRef?.current &&
			!filterDropdownRef.current.contains(e.target as Node) &&
			!filterButtonRef.current.contains(e.target as Node) &&
			!(
				e.target instanceof Element &&
				e.target.classList.contains(
					'send-all-modal-filter-dropdown-item-icon-cover'
				)
			)
		) {
			setOpenFilter(false);
		}
	};

	useEffect(() => {
		setInitialRender(true);
		setActiveBusiness(allBusinesses[0]);
		window.addEventListener('click', closeDropdown);

		return () => window.removeEventListener('click', closeDropdown);
	}, []);

	useEffect(() => {
		if (approvedBusinessList.length > 0) {
			const approvedBusinessIds = approvedBusinessList.map(
				(b: Business) => b._id
			);
			const approvedSet = new Set(approvedBusinessIds);
			filteredBusinesses.forEach((b: Business) => {
				if (approvedSet.has(b._id)) {
					document
						.querySelector(`.element-${b._id}`)
						?.classList.add('approved');
					document
						.querySelector(`.element-${b._id}`)
						?.classList.remove('unapproved');
					document
						.querySelector(`.toggle-${b._id}`)
						?.classList.remove('unapproved');
				}
			});
		}
	}, [filteredBusinesses.length]);

	const countScheduledBusinesses = () => {
		const scheduledBusinessesList = allBusinesses.filter(
			(b: Business) => scheduledEmailActivity[b._id]
		);
		setScheduledBusinesses(scheduledBusinessesList);
	};

	useEffect(() => {
		countScheduledBusinesses();

		if (sendAllSuccess) {
			setApprovedEmails([]);
			setApprovedBusinessList([]);

			setTimeout(() => {
				sendAllClear();
				setShowSendAllFinalModal(false);
			}, 1600);

			const businessIds = approvedBusinessList.map(item => item._id);

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'send_all_success',
				properties: {
					sendAllSuccess,
					numberOfBusinesses: approvedEmails.length,
					businessIds
				}
			});

			return;
		}

		if (sendAllFailure) {
			setTimeout(() => {
				sendAllClear();
			}, 2000);
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'send_all_failed',
				properties: {
					sendAllFailure
				}
			});
		}
	}, [sendAllSuccess, sendAllFailure]);

	useEffect(() => {
		if (cancelScheduledEmailSuccess) {
			countScheduledBusinesses();
		}
	}, [cancelScheduledEmailSuccess]);

	return (
		<div className="send-all-modal-holder">
			{displayAllDuplicates &&
				user.role !== UserRole.Free &&
				user.role !== UserRole.PreTrial && (
					<DisplayDuplicates data={duplicateGroup} />
				)}

			<span
				className="send-all-toggle"
				onClick={() => {
					setShowSendAll(false);
					setSendAllMode(!sendAllMode);
					classicMode();
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName:
							'user_clicked_go_back_to_classic_mode_send_all'
					});
				}}
			>
				Go Back to Classic Mode
				<SendAllToggleOn className="send-all-toggle-icon-on" />
			</span>
			{showSendAllFinalModal && (
				<>
					<div className="send-all-final-submission-background" />
					<div className="send-all-final-submission-holder">
						<div className="send-all-final-submission-title">
							Confirm sending all emails
						</div>
						<div className="send-all-final-submission-subtitle">
							Each scheduled email will be spaced out by a few
							minutes in between.
						</div>
						{!sendAllLoading && !sendAllSuccess && (
							<div
								className="send-all-final-submission-back-button"
								onClick={() => {
									setShowSendAllFinalModal(false);
								}}
							>
								Cancel
							</div>
						)}
						{sendAllFailure && (
							<span className="send-all-final-submission-failure-text">
								{sendAllFailure} Please try again.
							</span>
						)}
						<button
							type="button"
							className={`send-all-final-submission-send-button ${sendAllLoading ? 'loading' : ''} ${sendAllSuccess ? 'success' : ''} ${sendAllFailure ? 'failure' : ''} `}
							onClick={() => {
								const approvedEmailsFormated =
									approvedEmails.map((b: Email) => {
										return {
											...b,
											body: normalizeHTML(b.body)
										};
									});

								sendAll(approvedEmailsFormated);
								sendAnalytics({
									type: 'track',
									userId: user.email,
									eventName:
										'user_clicked_confirm_and_send_all'
								});
							}}
						>
							{!sendAllLoading &&
							!sendAllSuccess &&
							!sendAllFailure
								? 'Confirm & Send'
								: ''}
							{sendAllLoading && (
								<Loader
									className="send-all-final-submission-send-button-loading"
									status={Status.LOADING}
								/>
							)}
							{sendAllSuccess && (
								<CheckmarkGreenCircleFilled className="send-all-final-submission-send-button-success" />
							)}
							{sendAllFailure && (
								<ExclamationMarkRed className="send-all-final-submission-send-button-failure" />
							)}
						</button>
					</div>
				</>
			)}
			<CloseThin
				className="send-all-modal-close"
				onClick={() => {
					closeEmailModal();
					setCustomEmailModalDisplay(false);
					setShowSendAll(false);
				}}
			/>
			<div className="send-all-modal-header">
				<div className="send-all-modal-title">{list.name}</div>
				<div className="send-all-modal-list-name-subtitle">
					(Only businesses with emails appear)
				</div>
				<div className="send-all-modal-count-container">
					<div className="send-all-modal-display-count">
						Displaying <span>{filteredBusinesses.length} </span>{' '}
						businesses
					</div>
					<div className="send-all-modal-approved-count">
						<span>
							{approvedBusinessList.length} /{' '}
							{filteredBusinesses.length}{' '}
						</span>
						emails approved
					</div>
					<div className="send-all-modal-scheduled-count">
						<span>
							{scheduledBusinesses.length} /{' '}
							{filteredBusinesses.length}{' '}
						</span>
						emails scheduled
					</div>
					<div className="send-all-modal-approval-limit">
						Max approval limit of {monthlyEmailLimit}
					</div>
				</div>
				<div className="send-all-modal-header-right">
					<button
						type="button"
						ref={filterButtonRef}
						className={`send-all-modal-filter-button ${openFilter ? 'opened' : ''}`}
						onClick={() => setOpenFilter(!openFilter)}
					>
						Filter Businesses
						<FilterCircle className="send-all-modal-filter-button-circle-icon" />
					</button>
					{openFilter && (
						<div
							ref={filterDropdownRef}
							className="send-all-modal-filter-dropdown"
						>
							{filterOptions.map((filter, ind) => {
								const isApprovedSelected =
									selectedFilters.includes('Approved');
								const isNotApprovedSelected =
									selectedFilters.includes('Not approved');
								const isScheduledSelected =
									selectedFilters.includes('Scheduled');
								const isNotScheduledSelected =
									selectedFilters.includes('Not scheduled');
								const isContactedSelected =
									selectedFilters.includes('Contacted');
								const isNotContactedSelected =
									selectedFilters.includes('Not contacted');

								// Logic to disable each filter option
								let isDisabled = false;
								switch (ind) {
									case 0: // Approved
										isDisabled =
											isNotApprovedSelected ||
											isScheduledSelected;
										break;
									case 1: // Not approved
										isDisabled =
											isApprovedSelected ||
											isScheduledSelected;
										break;
									case 2: // Scheduled
										isDisabled =
											isNotScheduledSelected ||
											isApprovedSelected ||
											isNotApprovedSelected;
										break;
									case 3: // Not scheduled
										isDisabled =
											isScheduledSelected ||
											isApprovedSelected;
										break;
									case 4: // Contacted
										isDisabled = isNotContactedSelected;
										break;
									case 5: // Not contacted
										isDisabled = isContactedSelected;
										break;
									default:
										break;
								}

								const isSelected =
									selectedFilters.includes(filter);

								const handleClick = () => {
									if (isDisabled) {
										return;
									}

									sendAllModalLeftRef?.current?.scroll(0, 0);

									if (!isSelected) {
										setSelectedFilters([
											...selectedFilters,
											filter
										]);
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'user_filtered_businesses_send_all',
											properties: { filter }
										});
									} else {
										setSelectedFilters(
											selectedFilters.filter(
												x => x !== filter
											)
										);
										sendAnalytics({
											type: 'track',
											userId: user.email,
											eventName:
												'user_removed_filter_businesses_send_all',
											properties: { filter }
										});
									}
								};

								return (
									<div
										onKeyDown={e => {
											if (e.key === 'Enter') {
												handleClick();
											}
										}}
										role="button"
										tabIndex={0}
										className={`send-all-modal-filter-dropdown-item ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
										key={filter}
										onClick={handleClick}
									>
										<span className="send-all-modal-filter-dropdown-item-icon-cover" />
										{isSelected ? (
											<CheckboxChecked />
										) : (
											<CheckboxUnchecked />
										)}
										{filter}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
			<div className="send-all-modal-inner">
				<div ref={sendAllModalLeftRef} className="send-all-modal-left">
					{filteredBusinesses.map((b: Business, ind: number) => {
						return (
							<div
								id={b._id}
								key={b._id}
								data-position={ind}
								className={`send-all-modal-single-email-item element-${b._id} ${scheduledEmailActivity[b._id] ? 'scheduled' : 'approved unapproved'} ${activeBusiness?._id === b._id ? 'active' : ''} ${aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[`${b._id}`] && selectedTemplate?.body?.includes('[ai_snippet]') ? 'ai' : ''} ${aiLoading && !aiText?.[`${list.id}`]?.[`${selectedTemplate?.id}`]?.[`${b._id}`] && selectedTemplate?.body?.includes('[ai_snippet]') ? 'aiLoading' : ''} `}
								onClick={e => {
									setActiveBusiness(b);
								}}
							>
								<span>{b.name}</span>
								<div className="send-all-modal-single-email-item-icon-div">
									{b?.emailStatus &&
										scheduledEmailActivity[`${b?._id}`] && (
											<div className="send-all-modal-single-email-item-icon-group">
												<GoldCalendar className="send-all-modal-single-email-item-icon-group-scheduled-icon" />
												<LightbulbIcon className="send-all-modal-single-email-item-icon-group-contacted" />
												<span className="send-all-modal-single-email-item-icon-group-contacted-hovertext">
													{contactedCalloutText(
														b?.lastContact?.email
															?.date
													)}
												</span>
												<span className="send-all-modal-single-email-item-icon-group-scheduled-hovertext">
													An email is scheduled for
													this business on{' '}
													{`${dayjs(scheduledEmailActivity[`${b?._id}`]?.scheduleDate, 'MM/DD/YYYY, h:mm:ss A')?.format('ddd, MMM D')} at ${dayjs(scheduledEmailActivity[`${b?._id}`]?.scheduleDate, 'MM/DD/YYYY, h:mm:ss A')?.format('hh:mm a')}.`}
												</span>
											</div>
										)}
									{b?.lastContact?.email?.date &&
										!scheduledEmailActivity[`${b?._id}`] &&
										!aiText?.[`${list.id}`]?.[
											`${selectedTemplate?.id}`
										]?.[`${b._id}`] &&
										!selectedTemplate?.body?.includes(
											'[ai_snippet]'
										) && (
											<div className="send-all-modal-single-email-item-contacted">
												<LightbulbIcon className="send-all-modal-single-email-item-contacted-icon" />
												<span className="send-all-modal-single-email-item-contacted-hovertext">
													{contactedCalloutText(
														b.lastContact.email.date
													)}
												</span>
											</div>
										)}
									{aiText?.[`${list.id}`]?.[
										`${selectedTemplate?.id}`
									]?.[`${b._id}`] &&
										selectedTemplate?.body?.includes(
											'[ai_snippet]'
										) &&
										!scheduledEmailActivity[b._id] &&
										!b?.lastContact?.email?.date && (
											<ChatGPTUnfilled className="send-all-modal-single-email-item-chat-gpt-icon" />
										)}
									{aiText?.[`${list.id}`]?.[
										`${selectedTemplate?.id}`
									]?.[`${b._id}`] &&
										selectedTemplate?.body?.includes(
											'[ai_snippet]'
										) &&
										!scheduledEmailActivity[b._id] &&
										b?.lastContact?.email?.date && (
											<div className="send-all-modal-single-email-item-icon-group">
												<ChatGPTUnfilled className="send-all-modal-single-email-item-icon-group-chat-gpt" />
												<LightbulbIcon className="send-all-modal-single-email-item-icon-group-contacted" />
												<span className="send-all-modal-single-email-item-icon-group-contacted-hovertext">
													{contactedCalloutText(
														b.lastContact.email.date
													)}
												</span>
											</div>
										)}
									{aiLoading &&
										!aiText?.[`${list.id}`]?.[
											`${selectedTemplate?.id}`
										]?.[`${b._id}`] &&
										selectedTemplate?.body?.includes(
											'[ai_snippet]'
										) && (
											<Loader
												className="send-all-modal-single-email-item-ai-loading"
												status={Status.LOADING}
											/>
										)}
									{!scheduledEmailActivity[b._id] && (
										<>
											<span
												className={`send-all-modal-single-email-item-toggle toggle-${b._id} unapproved`}
												onClick={e => {
													e.stopPropagation();
													const el =
														document.querySelector(
															`.element-${b._id}`
														);
													const toggle =
														document.querySelector(
															`.toggle-${b._id}`
														);

													if (
														el?.classList.contains(
															'approved'
														) &&
														el?.classList.contains(
															'unapproved'
														) &&
														toggle?.classList.contains(
															'unapproved'
														)
													) {
														// If there's no AI Snippet generated here, don't approve it
														if (
															selectedTemplate?.body?.includes(
																'ai_snippet'
															) &&
															!aiText?.[
																`${list?.id}`
															]?.[
																`${selectedTemplate?.id}`
															]?.[`${b?._id}`]
														)
															return;

														toggle.classList.remove(
															'unapproved'
														);
														el.classList.remove(
															'unapproved'
														);
														collectApprovedEmails();
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'user_approved_business_send_all',
															properties: {
																business: {
																	_id: b?._id,
																	address:
																		b?.address,
																	name: b?.name
																},
																method: 'toggle'
															}
														});
														return;
													}

													if (
														el?.classList.contains(
															'approved'
														) &&
														!el?.classList.contains(
															'unapproved'
														) &&
														!toggle?.classList.contains(
															'unapproved'
														)
													) {
														toggle.classList.add(
															'unapproved'
														);
														el.classList.add(
															'unapproved'
														);
														collectApprovedEmails();
														sendAnalytics({
															type: 'track',
															userId: user.email,
															eventName:
																'user_unapproved_business_send_all',
															properties: {
																business: {
																	_id: b?._id,
																	address:
																		b?.address,
																	name: b?.name
																},
																method: 'toggle'
															}
														});
													}
												}}
											>
												<span className="send-all-modal-single-email-item-toggle-switch">
													<CheckMarkGreenUnfilled className="send-all-modal-single-email-item-toggle-switch-approved" />
													<EllipsisOrange className="send-all-modal-single-email-item-toggle-switch-unapproved" />
													<Toggle />
												</span>
											</span>
											{displayToggleHovertext(b)}
										</>
									)}
								</div>
							</div>
						);
					})}
				</div>
				<div className="send-all-modal-center">
					<BusinessPreviewEmailModal
						user={user}
						setDeleteDataIndex={setDeleteDataIndex}
						to={to}
						setTo={setTo}
						business={activeBusiness}
						editedEmails={editedEmails.current}
						editedEmailsTo={editedEmailsTo}
						setEditedEmailsTo={setEditedEmailsTo}
					/>
				</div>
				<div className="send-all-modal-right">
					{selectedTemplate?.body?.includes('[ai_snippet]') &&
						((aiLoading &&
							!aiText?.[`${list?.id}`]?.[
								`${selectedTemplate?.id}`
							]?.[`${activeBusiness?._id}`]) ||
							aiRegenerateLoading?.[`${list?.id}`]?.[
								`${selectedTemplate?.id}`
							]?.[`${activeBusiness?._id}`]) && (
							<div className="ai-loading-form-background">
								<Loader status={Status.LOADING} />
							</div>
						)}
					{aiCredits <= 0 &&
						!aiText?.[`${list?.id}`]?.[`${selectedTemplate?.id}`]?.[
							`${activeBusiness?._id}`
						] &&
						selectedTemplate?.body?.includes('[ai_snippet]') && (
							<>
								<div className="ai-credits-none-background" />
								<div className="ai-credits-none">
									You have used all your AI credits. Please
									choose a different template.
								</div>
							</>
						)}
					<input
						className={`send-all-modal-subject-input ${scheduledEmailActivity[`${activeBusiness?._id}`] ? 'disabled' : ''}`}
						placeholder="Subject"
						value={subject}
						disabled={
							scheduledEmailActivity[`${activeBusiness?._id}`]
						}
						onChange={e => {
							setSubject(e.target.value);

							editedEmails.current = {
								...editedEmails.current,
								[activeBusiness?._id]: {
									...editedEmails.current[
										`${activeBusiness?._id}`
									],
									subject: e.target.value
								}
							};
						}}
					/>
					<div className="send-all-email-input">
						<ContextMenu
							disabled
							offset={{ top: 32 }}
							onSelect={(item: string) =>
								setBodyContextMenuValue(item)
							}
							items={['business_name', 'list_name']}
							className="h-full rounded"
						>
							{() => (
								<RichEditor
									value={replaceVariables(body.current)}
									enablePropagation
									onChange={plainText => {
										body.current = plainText;
										editedEmails.current = {
											...editedEmails.current,
											[activeBusiness?._id]: {
												...editedEmails.current[
													`${activeBusiness?._id}`
												],
												body: plainText
											}
										};
									}}
									className="send-all-modal-rich-editor"
									disabled={
										scheduledEmailActivity[
											`${activeBusiness?._id}`
										]
									}
									contextMenuValue={bodyContextMenuValue}
								/>
							)}
						</ContextMenu>
					</div>
					<div className="send-all-modal-button-holder">
						<button
							className={`send-all-modal-approve-button ${approveButtonClass()} `}
							onClick={approveButtonClickHandler}
							type="button"
						>
							{displayApproveButtonText()}
						</button>
						{scheduledEmailActivity[activeBusiness?._id] && (
							<span className="send-all-modal-approve-button-hovertext">
								An email is already scheduled for this business.
								Once it is sent or cancelled you will be able to
								approve it again.
							</span>
						)}
						{scheduledEmailActivity[activeBusiness?._id] && (
							<button
								className="scheduled-send-cancel-button"
								onClick={openScheduledSendCancelModal}
							>
								<TrashFullSquare className="scheduled-send-cancel-icon" />
								Cancel scheduled
							</button>
						)}
						<button
							type="button"
							className={`ai-button regenerate ${
								aiCredits <= 0 &&
								aiRegenerated?.[`${list?.id}`]?.[
									`${selectedTemplate?.id}`
								]?.[`${activeBusiness?._id}`]
									? 'disabled'
									: ''
							} ${
								!selectedTemplate?.body?.includes(
									'[ai_snippet]'
								) ||
								scheduledEmailActivity[`${activeBusiness?._id}`]
									? 'hide'
									: ''
							} ${
								!aiRegenerated?.[`${list?.id}`]?.[
									`${selectedTemplate?.id}`
								]?.[`${activeBusiness?._id}`]
									? 'free'
									: ''
							} ${
								aiCredits > 0 &&
								aiRegenerated?.[`${list?.id}`]?.[
									`${selectedTemplate?.id}`
								]?.[`${activeBusiness?._id}`]
									? 'not-free'
									: ''
							} `}
							onClick={() => {
								if (activeBusiness && selectedTemplate) {
									regenAISnippet(
										activeBusiness?._id,
										list?.id,
										selectedTemplate,
										aiText?.[`${list?.id}`]?.[
											`${selectedTemplate?.id}`
										]?.[`${activeBusiness?._id}`].id
									);

									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName: `Regenerated AI Snippet`,
										properties: {
											free: !aiRegenerated?.[
												`${list?.id}`
											]?.[`${selectedTemplate?.id}`]?.[
												`${activeBusiness?._id}`
											]
										}
									});
								}
							}}
							disabled={
								aiCredits <= 0 &&
								aiRegenerated?.[`${list?.id}`]?.[
									`${selectedTemplate?.id}`
								]?.[`${activeBusiness?._id}`]
							}
						>
							Regenerate AI
							<ChatGPT />
							{aiRegenerateFailure && (
								<span className="ai-button-failure-text">
									<ExclamationMarkRed className="ai-button-failure-icon" />
									Regeneration failed. Try again.
								</span>
							)}
						</button>
						{aiText?.[`${list?.id}`]?.[`${selectedTemplate?.id}`]?.[
							`${activeBusiness?._id}`
						] &&
							!aiRegenerated?.[`${list?.id}`]?.[
								`${selectedTemplate?.id}`
							]?.[`${activeBusiness?._id}`] &&
							!scheduledEmailActivity[
								`${activeBusiness?._id}`
							] && (
								<span className="ai-regenerate-free sendAll">
									1st free!
								</span>
							)}
					</div>
				</div>
				{selectedTemplate?.body?.includes('[ai_snippet]') && (
					<div className="send-all-ai-credit-limit">
						AI Credits
						<span>{aiCredits}</span>
					</div>
				)}
				<div className="send-all-modal-bottom-button-holder">
					<button
						className="send-all-back-button"
						onClick={() => {
							setShowSendAll(false);
							const el = document.querySelector('.modali');
							el?.classList.remove('hide');
						}}
					>
						Go back
					</button>
					<button
						className="send-all-approve-all-button"
						onClick={() => {
							approveAll();
							setDisplayAllDuplicates(true);
							sendAnalytics({
								type: 'track',
								userId: user.email,
								eventName: 'user_clicked_approve_all_send_all'
							});
						}}
					>
						Approve all
					</button>
					{monthlyEmailLimit < filteredBusinesses.length && (
						<span className="send-approve-all-button-hovertext">
							This will approve only the top {monthlyEmailLimit}{' '}
							emails in your list due to the max approval limit.
						</span>
					)}
					<span className="send-all-tool-tip">
						<img
							src={QuestionMarkPNG}
							className="send-all-tool-tip-icon"
						/>
						<span className="send-all-tool-tip-text">
							1 - We schedule emails up to one month in advance.
							<br />
							<br />
							2 - Emails are scheduled exclusively on weekdays
							from 7 AM to 7 PM, according to your timezone.
							<br />
							<br />
							3 - There is a random 2 to 6-minute interval between
							each scheduled email.
							<br />
							<br />4 - We schedule as many emails as possible per
							day considering the rules above and your daily email
							limit.
						</span>
					</span>
					<button
						type="button"
						className={`send-all-approved-emails-button ${nylasSynced === false || approvedEmails.length === 0 || approvedEmails.length > approveLimit ? 'disabled' : ''} `}
						onClick={() => {
							if (
								nylasSynced &&
								approvedEmails.length > 0 &&
								approvedEmails.length <= approveLimit
							) {
								setShowSendAllFinalModal(true);
								collectApprovedEmails();
								sendAnalytics({
									type: 'track',
									userId: user.email,
									eventName:
										'user_clicked_send_all_approved_emails'
								});
							}
						}}
					>
						Send all approved emails
					</button>
					{nylasSynced == false && (
						<div className="nylas-sync-hovertext-holder-send-all">
							<span className="nylas-sync-hovertext-send-all">
								You are currently disconnected from Nylas,
								please re-verify your email address to continue
								sending emails. If you recently verified, please
								wait 10 minutes before attempting to reverify.
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SendAllModal;
